import { useEffect, useCallback, useState, useRef } from 'react';
import { useOfflineStorage } from './useOfflineStorage';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import { base64ToFile } from '@/utils/offlineFileHandler';
import { ConflictData } from '@/components/ConflictResolutionModal';

export const useOfflineSync = () => {
  const { getPendingOperations, clearPendingOperation, isOnline } = useOfflineStorage();
  const { user } = useAuth();
  const { toast } = useToast();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [conflicts, setConflicts] = useState<ConflictData[]>([]);
  const syncingRef = useRef(false);

  const updatePendingCount = useCallback(async () => {
    const pendingOps = await getPendingOperations();
    setPendingCount(pendingOps.length);
  }, [getPendingOperations]);

  const syncPendingOperations = useCallback(async (skipConflictCheck = false) => {
    if (!isOnline || !user) return;

    if (syncingRef.current) {
      console.log('Ya hay una sincronización en proceso, saltando...');
      return;
    }

    syncingRef.current = true;
    let safetyTimeout: number | undefined;

    try {
      const pendingOps = await getPendingOperations();
      if (pendingOps.length === 0) {
        setPendingCount(0);
        return;
      }

      setIsSyncing(true);
      // Safety: evita quedarse pegado
      safetyTimeout = window.setTimeout(() => {
        console.warn('Forzando fin de sincronización por timeout');
        setIsSyncing(false);
      }, 30000);

      let successCount = 0;
      let failCount = 0;
      const detectedConflicts: ConflictData[] = [];

    for (const op of pendingOps) {
      let retries = 3;
      let success = false;
      
      // Eliminar la operación pendiente ANTES de intentar ejecutarla
      // para evitar duplicaciones si hay múltiples llamadas
      await clearPendingOperation(op.id);

      while (retries > 0 && !success) {
        try {
          // Detectar conflictos para operaciones UPDATE
          if (op.operation === 'update' && !skipConflictCheck) {
            const { data: serverData, error: fetchError } = await (supabase.from as any)(op.table)
              .select('*')
              .eq('id', op.data.id)
              .single();

            if (!fetchError && serverData) {
              const serverTimestamp = new Date(serverData.updated_at).getTime();
              const localTimestamp = op.timestamp;

              // Si el servidor tiene una versión más reciente, hay conflicto
              if (serverTimestamp > localTimestamp) {
                detectedConflicts.push({
                  id: op.id,
                  operation: 'update',
                  table: op.table,
                  localData: op.data,
                  serverData,
                  localTimestamp,
                  serverTimestamp: serverData.updated_at,
                });
                
                // Marcar como procesado temporalmente (no sincronizar aún)
                success = true;
                continue;
              }
            }
          }

          // Si la operación incluye un archivo base64, subirlo primero
          if (op.data.fileData) {
            const { fileData, ...dataWithoutFile } = op.data;
            const file = base64ToFile(fileData.base64, fileData.filename, fileData.mimeType);
            
            // Subir archivo a Storage
            const filePath = `${user.id}/${Date.now()}_${fileData.filename}`;
            const { error: uploadError } = await supabase.storage
              .from(getBucketName(op.table))
              .upload(filePath, file);

            if (uploadError) throw uploadError;

            // Obtener URL pública
            const { data: { publicUrl } } = supabase.storage
              .from(getBucketName(op.table))
              .getPublicUrl(filePath);

            // Actualizar datos con URL del archivo
            op.data = {
              ...dataWithoutFile,
              url_archivo: publicUrl,
              ruta_storage: filePath,
            };
          }

          switch (op.operation) {
            case 'create':
              // Omitir IDs locales o campos temporales al crear en DB
              const { id: _localId, _tempId: tempIdForCache, ...insertPayload } = op.data || {};
              const { data: insertedData, error: insertError } = await (supabase.from as any)(op.table)
                .insert([insertPayload])
                .select()
                .single();
              
              if (insertError) throw insertError;
              
              // Actualizar caché si existe _tempId (solo cuando lo tengamos)
              if (tempIdForCache && insertedData) {
                const cacheKey = getCacheKeyForTable(op.table);
                if (cacheKey) {
                  const cachedData = await getCachedDataFromDB(cacheKey);
                  if (cachedData && Array.isArray(cachedData)) {
                    // Reemplazar el registro temporal con el real
                    const updatedCache = cachedData.map((item: any) => 
                      item.id === tempIdForCache ? insertedData : item
                    );
                    await cacheDataToDB(cacheKey, updatedCache);
                  }
                }
              }
              break;
            case 'update':
              const { id, _tempId, ...updateData } = op.data;
              await (supabase.from as any)(op.table).update(updateData).eq('id', id);
              break;
            case 'delete':
              await (supabase.from as any)(op.table).delete().eq('id', op.data.id);
              break;
          }
          
          // Ya eliminamos la operación al inicio, solo marcamos éxito
          successCount++;
          success = true;
        } catch (error) {
          console.error(`Error syncing operation (${retries} retries left):`, error);
          retries--;
          if (retries > 0) {
            // Esperar antes de reintentar (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, (4 - retries) * 1000));
          } else {
            failCount++;
          }
        }
      }
    }

    // Si hay conflictos, mostrarlos
    if (detectedConflicts.length > 0) {
      setConflicts(detectedConflicts);
      return;
    }

    if (successCount > 0) {
      toast({
        title: "Sincronización completa",
        description: `${successCount} cambio${successCount > 1 ? 's' : ''} sincronizado${successCount > 1 ? 's' : ''}`,
      });
      
      // Disparar evento personalizado para que los componentes recarguen datos
      window.dispatchEvent(new CustomEvent('offline-sync-complete', { 
        detail: { successCount, tables: [...new Set(pendingOps.map(op => op.table))] }
      }));
    }

    if (failCount > 0) {
      toast({
        title: "Error de sincronización",
        description: `${failCount} operación${failCount > 1 ? 'es' : ''} no se pud${failCount > 1 ? 'ieron' : 'o'} sincronizar`,
        variant: "destructive",
      });
    }

  } finally {
    if (safetyTimeout) {
      clearTimeout(safetyTimeout);
    }
    setIsSyncing(false);
    syncingRef.current = false;
    await updatePendingCount();
  }
  }, [isOnline, user, getPendingOperations, clearPendingOperation, toast, updatePendingCount]);

  useEffect(() => {
    updatePendingCount();
    // Check every 5 seconds for pending operations
    const interval = setInterval(updatePendingCount, 5000);
    return () => clearInterval(interval);
  }, [updatePendingCount]);

  useEffect(() => {
    if (isOnline && user) {
      syncPendingOperations();
    }
  }, [isOnline, user]);

  const resolveConflict = useCallback(async (conflictId: string, useLocal: boolean) => {
    const conflict = conflicts.find(c => c.id === conflictId);
    if (!conflict) return;

    try {
      if (useLocal) {
        // Usar la versión local: actualizar el servidor
        const { id, ...updateData } = conflict.localData;
        await (supabase.from as any)(conflict.table)
          .update(updateData)
          .eq('id', id);
      }
      // Si usa versión del servidor, simplemente no hacemos nada (ya está en el servidor)
      
      // Limpiar la operación pendiente
      await clearPendingOperation(conflictId);
      
      // Remover conflicto de la lista
      setConflicts(prev => prev.filter(c => c.id !== conflictId));

      toast({
        title: "Conflicto resuelto",
        description: `Se usó la versión ${useLocal ? 'local' : 'del servidor'}`,
      });
    } catch (error) {
      console.error('Error resolving conflict:', error);
      toast({
        title: "Error",
        description: "No se pudo resolver el conflicto",
        variant: "destructive",
      });
    }
  }, [conflicts, clearPendingOperation, toast]);

  const resolveAllConflicts = useCallback(async (useLocal: boolean) => {
    for (const conflict of conflicts) {
      await resolveConflict(conflict.id, useLocal);
    }
    setConflicts([]);
    
    // Reintentar sincronización sin chequeo de conflictos
    await syncPendingOperations(true);
  }, [conflicts, resolveConflict, syncPendingOperations]);

  return { 
    syncPendingOperations, 
    pendingCount, 
    isSyncing, 
    conflicts,
    resolveConflict,
    resolveAllConflicts,
  };
};

// Helper para determinar el bucket según la tabla
const getBucketName = (table: string): string => {
  const bucketMap: Record<string, string> = {
    'documentos_legales': 'documentos-legales',
    'documentos_sociales': 'documentos-sociales',
    'documentos_gdb': 'documentos-gdb',
    'evidencias_cumplimiento': 'evidencias-cumplimiento',
    'evidencias_archivos': 'actos-administrativos',
  };
  return bucketMap[table] || 'documentos-legales';
};

// Helper para obtener la clave de caché según la tabla
const getCacheKeyForTable = (table: string): string | null => {
  const cacheKeyMap: Record<string, string> = {
    'obligaciones_ambientales': 'all',
    'documentos_legales': 'all',
    'documentos_sociales': 'all',
    'documentos_gdb': 'all',
    'cumplimientos_documentos': 'all',
    'evidencias_archivos': 'all',
    'evidencias_cumplimiento': 'all',
    'planes_manejo': 'all',
  };
  return cacheKeyMap[table] || null;
};

// Helpers para acceder a IndexedDB directamente (usados durante la sincronización)
const getCachedDataFromDB = async (key: string): Promise<any> => {
  try {
    const db = await import('idb').then(({ openDB }) => 
      openDB('viabiliza_offline_db', 1)
    );
    return await db.get('obligaciones', key);
  } catch (error) {
    console.error('Error getting cached data:', error);
    return null;
  }
};

const cacheDataToDB = async (key: string, data: any): Promise<void> => {
  try {
    const db = await import('idb').then(({ openDB }) => 
      openDB('viabiliza_offline_db', 1)
    );
    await db.put('obligaciones', data, key);
  } catch (error) {
    console.error('Error caching data:', error);
  }
};
